<script>
import { VueDraggable } from 'vue-draggable-plus';
import { put } from '@rails/request.js';

export default {
  props: {
    eventId: String,
    divisionsUrl: String,
    volunteersUrl: String,
    volunteerUrl: String,
  },
  data() {
    return {
      divisions: [],
      collapseKey: "divisionKanBanCollapseState",
      collapseState: {},
    }
  },
  async mounted() {
    this.$cable.subscribe({
      channel: "EventsChannel",
      eventId: this.eventId,
    });

    await this.fetchDivisions();
    await this.fetchVolunteers();
    
    const stored = localStorage.getItem(this.collapseKey);
    if (stored) {
      this.collapseState = JSON.parse(stored);
    }
  },
  components: {
    VueDraggable
  },
  watch: {
    collapseState: {
      handler(newValue, oldValue) {
        localStorage.setItem(this.collapseKey, JSON.stringify(this.collapseState))
      },
      deep: true
    }
  },
  channels: {
    EventsChannel: {
      received(data) {
        switch (data.event) {
          case "volunteers_changed":
            for (const volunteerId of data.volunteer_ids) {
              this.refreshVolunteer(volunteerId)
            }
            break;
        }
      }
    },
  },
  methods: {
    isDivisionLackingVolunteers(division) {
      return division.volunteers.length < division.minimum_volunteers_count
    },
    isDivisionCollapsed(divisionId) {
      return this.collapseState[divisionId]
    },
    toggleDivision(divisionId) {
      this.collapseState[divisionId] = !this.collapseState[divisionId];
    },
    sortedVolunteers(volunteers) {
      // return a new, sorted array (case-insensitive)
      return volunteers.sort((a, b) => 
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
      );
    },
    divisionIndexById(divisionId) {
      return this.divisions.findIndex(d => d.id === divisionId)
    },
    async onChange(evt) {
      const divisionId = evt.to.getAttribute('data-division-id');
      const volunteer = evt.data
      const response = await put(volunteer.updateUrl, { body: JSON.stringify({
        volunteer: {
          division_id: divisionId
        }
      })})
    },
    async fetchDivisions() {
      // Step 1: Fetch the divisions themselves
      const divisionsResponse = await fetch(this.divisionsUrl);
      const divisionsList = await divisionsResponse.json();
      // Initialize divisions with empty cards arrays
      this.divisions = divisionsList.map(division => ({
        ...division,
        volunteers: []
      }));
    },
    async fetchVolunteers() {
      const response = await fetch(this.volunteersUrl)
      const volunteers = response.ok ? await response.json() : []

      volunteers.forEach(volunteer => {
        const divisionIndex = this.divisionIndexById(volunteer.divisionId)
        this.divisions[divisionIndex].volunteers.push(volunteer);
      });
    },
    refreshVolunteer: async function(volunteerId) {
      const response = await fetch(this.volunteerUrl.replace('__id__', volunteerId))
      if (!response.ok) {
        return
      }
      const volunteer = await response.json()

      this.divisions.forEach(division => {
        // Remove volunteer from any division they might be in in case they have been moved
        division.volunteers = division.volunteers.filter(v => v.id !== volunteer.id)
        // Readd updated one if we are on the right division
        if (division.id === volunteer.divisionId) {
          division.volunteers.push(volunteer)
        }
      })
    },
  }
};
</script>

<template>
  <div v-if="divisions.length > 0" class="kanban">
    <div v-for="division in divisions"
        :key="division.id"
        :style="{
          'border-top-color': division.color,
          'border-top-width': '0.3rem'
        }"
        :class="{
          collapsed: isDivisionCollapsed(division.id),
          'kanban-card-danger': isDivisionLackingVolunteers(division)
        }"
        class="kanban-card"
    >
      <div class="kanban-header">
        <button
          class="btn btn-sm btn-light bi"
          :class="[isDivisionCollapsed(division.id) ? 'bi-caret-right' : 'bi-caret-down']"
          @click="toggleDivision(division.id)"
        >
        </button>
        {{ division.name }}
        <span v-if="division.allrounders"
              :title="this.$i18n.t('divisions.attributes.allrounders')"
              class="bi-icon bi-person-arms-up me-1">
        </span>
        <div v-if="!isDivisionCollapsed(division.id)" class="float-end">
          <span :class="{'text-danger': isDivisionLackingVolunteers(division)}">
            {{ division.volunteers.length }}
          </span>
          <span v-if="division.minimum_volunteers_count > 0">/{{ division.minimum_volunteers_count }}</span>
          <span class="bi bi-person-fill ms-1"></span>
          <a v-if="division.editUrl" :href="division.editUrl" class="btn btn-sm btn-light border bi-icon bi-pencil ms-1"></a>
        </div>
      </div>

      <VueDraggable
        :data-division-id="division.id"
        :delay="300"
        :delayOnTouchOnly="true"
        @add="onChange"
        v-model="division.volunteers"
        group="divisions"
        item-key="id"
        class="kanban-list"
        filter=".draggable-disabled"
      >
        <div
          v-for="volunteer in sortedVolunteers(division.volunteers)"
          :key="volunteer.id"
          class="kanban-item d-flex align-items-stretch"
          :class="{'draggable-disabled': !volunteer.updateUrl}"
        >
          <span :style="{color: volunteer.color}" class="bi-icon bi-circle-fill me-1"></span>
          <div>
            <span v-if="volunteer.isPresent" class="bi bi-person-fill-check text-success me-1" :title="this.$i18n.t('timetable.volunteers.present')"></span>
            <span v-else class="bi bi-person-fill-slash text-danger me-1" :title="this.$i18n.t('timetable.volunteers.absent')"></span>
            <a :href="volunteer.url">{{ volunteer.title }}</a>
            <div class="small">
              <div v-if="volunteer.comment">{{ volunteer.comment }}</div>
              <VueCustomAttributeValues :customAttributeValues="volunteer.visibleCustomAttributeValues" />
            </div>
          </div>
        </div>
      </VueDraggable>
    </div>
  </div>
</template>